import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AlertTriangle, CheckCircle2, Clock3, MapPin, Hammer } from "lucide-react";
import { fmtDateTime } from "../lib/datetime";

export default function NRSAck() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [eta, setEta] = useState("45");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Missing acknowledgement token in URL.");
      setLoading(false);
      return;
    }
    api.get("/nrs/ack/check", { params: { token } })
      .then((r) => { setInfo(r.data); setLoading(false); })
      .catch((e) => { setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load this alert."); setLoading(false); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const n = parseInt(eta, 10);
    if (!n || n < 1 || n > 24 * 60) { setError("ETA must be between 1 minute and 24 hours."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/nrs/ack/confirm", { token, eta_minutes: n });
      setSuccess(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to acknowledge.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="nrs-ack-page" style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="card" style={{ background: "white", maxWidth: 520, width: "100%", padding: 24, borderRadius: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ background: "#dc2626", color: "#fff", padding: "6px 10px", borderRadius: 3, fontFamily: "IBM Plex Mono, monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em" }}>CALLOUT</div>
          <div className="mono" style={{ color: "#ff5a00", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em" }}>NEWELL RESPONSE SYSTEM</div>
        </div>

        {loading ? <div style={{ color: "#64748b" }}>Validating link…</div> : null}

        {!loading && error && !info ? (
          <div data-testid="nrs-ack-error-block" className="card" style={{ padding: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}>
            <AlertTriangle size={16} /><span>{error}</span>
          </div>
        ) : null}

        {!loading && info && !success ? (
          <>
            <h1 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 900 }}>Hi {info.recipient_name},</h1>
            <p style={{ margin: "0 0 18px", color: "#334155", fontSize: 14, lineHeight: 1.5 }}>You are on call for McCain Whittlesey. Acknowledge with your ETA — McCain only sees the positive confirmation once you do.</p>

            <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: 0, marginBottom: 18, overflow: "hidden" }}>
              <Row icon={<MapPin size={14} />} label="Area" value={info.area} />
              <Row icon={<Hammer size={14} />} label="Structure" value={info.structure_ref} />
              <Row icon={<AlertTriangle size={14} />} label="Description" value={info.description || "(not supplied)"} />
              <Row icon={<Clock3 size={14} />} label="Call received" value={info.received_at ? fmtDateTime(info.received_at) : "—"} mono />
            </div>

            <form onSubmit={submit}>
              <label style={{ display: "block", marginBottom: 12 }}>
                <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>YOUR ETA (MINUTES)</div>
                <input data-testid="nrs-ack-eta" className="input-base" type="number" min={1} max={1440} value={eta} onChange={(e) => setEta(e.target.value)} required autoFocus style={{ fontSize: 24, fontWeight: 900, padding: "10px 14px" }} />
                <div style={{ marginTop: 6, fontSize: 11, color: "#64748b" }}>e.g. 45 means &quot;on site in ~45 minutes&quot;.</div>
              </label>
              {error ? <div data-testid="nrs-ack-error" className="card" style={{ padding: 8, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", fontSize: 12 }}><AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{error}</div> : null}
              <button data-testid="nrs-ack-submit" className="btn-primary" type="submit" disabled={submitting} style={{ width: "100%", padding: "14px 18px", fontSize: 14, fontWeight: 700, letterSpacing: "0.04em" }}>
                {submitting ? "Confirming…" : "Acknowledge & confirm ETA"}
              </button>
            </form>
            <p style={{ margin: "16px 0 0", fontSize: 11, color: "#94a3b8" }}>This is a single-use link. The 60-minute response clock is already running.</p>
          </>
        ) : null}

        {success ? (
          <div data-testid="nrs-ack-success" style={{ marginTop: 4 }}>
            <div className="card" style={{ padding: 16, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <CheckCircle2 size={22} />
              <div>
                <div style={{ fontWeight: 900, fontSize: 16 }}>Acknowledged.</div>
                <div style={{ marginTop: 4, fontSize: 13 }}>McCain has been confirmed: <strong>{success.responder_name}</strong> is responding, on site in ~<strong>{success.eta_minutes} minutes</strong>.</div>
              </div>
            </div>
            <p style={{ margin: "16px 0 0", fontSize: 12, color: "#64748b" }}>You can close this tab. Use the in-app &quot;Mark on-site&quot; button on arrival to stop the 60-minute clock.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Row({ icon, label, value, mono }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", padding: "10px 14px", borderBottom: "1px solid #f1f5f9", alignItems: "flex-start", gap: 12 }}>
      <div className="mono" style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>{icon}{label}</div>
      <div style={{ color: "#0f172a", fontWeight: 600, fontFamily: mono ? "IBM Plex Mono, monospace" : "inherit", fontSize: mono ? 13 : 14 }}>{value}</div>
    </div>
  );
}
